// The arrows on the design view: where they attach, and how they get there.
//
// Pure geometry, kept out of the component so it can be reasoned about — and
// checked — without a DOM. `test-script/verify_trail_wires.mjs` runs every rule
// below against plain data.
//
// Two things this file exists to get right.
//
// **An arrow points at a RECORD.** A step holding five plans has to say which
// of them the lead raised, so a wire attaches at the middle of a record's own
// row rather than at the box. Where that record is behind the box's "N more",
// its wire lands on THAT row — which says how many it is holding — and every
// hidden sibling's wire lands there too, as one arrow rather than a bundle
// pointing at the same pixel.
//
// **One source draws ONE connector.** A record that raised five others gets a
// trunk out of its row, a spine, and a short branch with its own head into each
// of them. Five separate arrows leaving one row were five pictures of one fact.
// The exception is direction: a connector leaves by ONE edge, so targets to the
// right, to the left and in the same column are three connectors by definition,
// never one that leaves twice.

/** The width every step box is drawn at — the arrows land on its edges. */
export const BOX_W = 296
/** The channel a same-column arrow runs down, outside the boxes. */
export const LANE = 30

/** Where an arrow attaches: the middle of a record's row on a box's edge. */
export interface Port {
  step: string
  x: number
  y: number
}

export interface Wire {
  id: string
  d: string
  /** The arrowhead's polygon, on branches only — a trunk and a spine have none. */
  head?: string
  color: string
  width: number
  /** The dot that fastens a wire to what it leaves: without it the line reads
   *  as hanging in the gap rather than starting at the record. */
  nx?: number
  ny?: number
  nr?: number
}

/** Just enough of a step box to draw an arrow between two of them. */
export interface WireBox {
  id: string
  x: number
  y: number
  h: number
}

export interface Ghost {
  id: string
  d: string
  head: string
  label?: string
  lx: number
  ly: number
}

// ── arrowheads, by the direction they point ─────────────────────────────────

export function headRight(x: number, y: number): string {
  return `${x - 8},${y - 5} ${x},${y} ${x - 8},${y + 5}`
}
export function headLeft(x: number, y: number): string {
  return `${x + 8},${y - 5} ${x},${y} ${x + 8},${y + 5}`
}
export function headDown(x: number, y: number): string {
  return `${x - 5},${y - 8} ${x},${y} ${x + 5},${y - 8}`
}
export function headUp(x: number, y: number): string {
  return `${x - 5},${y + 8} ${x},${y} ${x + 5},${y + 8}`
}

type Dir = 'fwd' | 'back' | 'lane'

interface Group {
  from: Port
  dir: Dir
  hot: boolean
  to: Map<string, Port>
}

export interface WireResult {
  wires: Wire[]
  /** `from>to` for every STEP pair a record actually walked, so the designed
   *  arrow between those two steps is not drawn a second time on top. */
  joined: Set<string>
}

/**
 * Every connector on the diagram, from the chain's own parent → child pairs.
 *
 * `portOf` answers where a record key attaches — its own row, or the row that
 * stands in for it when it is hidden. A key it cannot place is skipped rather
 * than guessed at.
 */
export function buildWires(
  links: Array<{ from: string; to: string }>,
  portOf: (key: string) => Port | null,
  opts: { selectedKey?: string; accent: string; wire: string },
): WireResult {
  const groups = new Map<string, Group>()
  const joined = new Set<string>()

  for (const link of links) {
    const a = portOf(link.from)
    const b = portOf(link.to)
    // A record whose parent is hidden in the same box as itself has nothing to
    // draw: both ends resolved to the one row.
    if (!a || !b || (a.step === b.step && a.y === b.y)) continue
    joined.add(`${a.step}>${b.step}`)
    const dir: Dir = a.x === b.x ? 'lane' : (b.x > a.x ? 'fwd' : 'back')
    const gk = `${a.step}:${a.y}|${dir}`
    let g = groups.get(gk)
    if (!g) {
      g = { from: a, dir, hot: false, to: new Map() }
      groups.set(gk, g)
    }
    g.to.set(`${b.step}:${b.y}`, b)
    if (link.from === opts.selectedKey || link.to === opts.selectedKey) g.hot = true
  }

  const wires: Wire[] = []
  let n = 0
  groups.forEach((g) => {
    const targets = [...g.to.values()]
    const color = g.hot ? opts.accent : opts.wire
    const width = g.hot ? 2 : 1.4
    const near = targets[0]
    // Which edge the trunk leaves by, and where the spine stands: in the gap
    // between the two columns for a step forward or back, and in the channel
    // beside the box when both records sit in the same column.
    const from = g.dir === 'back' ? g.from.x : g.from.x + BOX_W
    const spine = g.dir === 'lane'
      ? g.from.x + BOX_W + LANE
      : g.dir === 'fwd'
        ? g.from.x + BOX_W + Math.round((near.x - g.from.x - BOX_W) / 2)
        : near.x + BOX_W + Math.round((g.from.x - near.x - BOX_W) / 2)
    const ys = [g.from.y, ...targets.map((t) => t.y)]
    const y0 = Math.min(...ys)
    const y1 = Math.max(...ys)

    // the trunk, out of the record that raised them
    wires.push({
      id: `t${n}`,
      color,
      width,
      d: `M ${from} ${g.from.y} L ${spine} ${g.from.y}`,
      nx: from,
      ny: g.from.y,
      nr: g.hot ? 3.2 : 2.6,
    })
    if (y1 - y0 > 1) {
      wires.push({ id: `s${n}`, color, width, d: `M ${spine} ${y0} L ${spine} ${y1}` })
    }
    targets.forEach((t, i) => {
      const enterLeft = g.dir === 'fwd'
      const tip = enterLeft ? t.x : t.x + BOX_W
      const end = enterLeft ? tip - 8 : tip + 8
      wires.push({
        id: `b${n}-${i}`,
        color,
        width,
        d: `M ${spine} ${t.y} L ${end} ${t.y}`,
        head: enterLeft ? headRight(tip, t.y) : headLeft(tip, t.y),
        // Where a connector forks, the fork is marked; a single branch is just
        // the trunk carrying on, and a dot on it would be noise.
        ...(targets.length > 1 ? { nx: spine, ny: t.y, nr: 2.2 } : null),
      })
    })
    n += 1
  })

  return { wires, joined }
}

/**
 * Box to box, for a designed arrow no record has walked.
 *
 * It still belongs on the picture — the process says this can happen and
 * nothing yet has — so it is drawn between the BOXES, muted and dashed, where
 * it cannot be mistaken for something that did.
 */
export function ghostEdge(id: string, a: WireBox, b: WireBox, label?: string): Ghost {
  if (a.x === b.x) {
    const x = a.x + 64
    const down = a.y < b.y
    const y1 = down ? a.y + a.h : a.y
    const y2 = down ? b.y - 8 : b.y + b.h + 8
    return {
      id,
      d: `M ${x} ${y1} L ${x} ${y2}`,
      head: down ? headDown(x, b.y) : headUp(x, b.y + b.h),
      label,
      lx: x + 56,
      ly: (y1 + y2) / 2,
    }
  }
  const forward = a.x < b.x
  const ay = a.y + Math.min(a.h / 2, 22)
  const by = b.y + Math.min(b.h / 2, 22)
  const x1 = forward ? a.x + BOX_W : a.x
  const x2 = forward ? b.x - 8 : b.x + BOX_W + 8
  const mid = Math.round((x1 + x2) / 2)
  return {
    id,
    d: `M ${x1} ${ay} L ${mid} ${ay} L ${mid} ${by} L ${x2} ${by}`,
    head: forward ? headRight(b.x, by) : headLeft(b.x + BOX_W, by),
    label,
    lx: mid,
    ly: (ay + by) / 2,
  }
}
